import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Tooltip, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { Driver } from '../types';
import { FETCH_ALL_COURIERS } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';
import { getStatusColor, formatDate } from '../utils';

interface PreAssignedDriversSelectorProps {
  selectedDriverIds: string[];
  onDriverIdsChange: (driverIds: string[]) => void;
  drivers?: Driver[];
  driversLoading?: boolean;
  hasMoreDrivers?: boolean;
  onLoadMoreDrivers?: () => void;
  externalSearchQuery?: string;
  onExternalSearchChange?: (query: string) => void;
  assignedDriverIds?: string[];
  assignedDriverStatuses?: { [driverId: string]: string }; // Map of driver ID to assignment status
  remainingSlots?: number;
}

const DRIVER_LIMIT = 50;

const PreAssignedDriversSelector: React.FC<PreAssignedDriversSelectorProps> = ({
  selectedDriverIds,
  onDriverIdsChange,
  drivers: providedDrivers,
  driversLoading: providedDriversLoading = false,
  hasMoreDrivers: providedHasMoreDrivers = false,
  onLoadMoreDrivers,
  externalSearchQuery,
  onExternalSearchChange,
  assignedDriverIds = [],
  assignedDriverStatuses = {},
  remainingSlots,
}) => {
  const [driverSearchQuery, setDriverSearchQuery] = useState<string>('');
  const [debouncedDriverSearchQuery, setDebouncedDriverSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState('');
  const driverListRef = useRef<HTMLDivElement>(null);
  const isFetchingMoreRef = useRef(false);
  const [isLoadingMoreDrivers, setIsLoadingMoreDrivers] = useState(false);

  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : driverSearchQuery;
  const setSearchQuery = onExternalSearchChange || setDriverSearchQuery;

  useEffect(() => {
    if (externalSearchQuery === undefined) {
      const timer = setTimeout(() => {
        setDebouncedDriverSearchQuery(driverSearchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [driverSearchQuery, externalSearchQuery]);

  const { data: driversData, loading: driversLoading, fetchMore: fetchMoreDrivers } = useQuery(FETCH_ALL_COURIERS, {
    ...stagingOptions,
    variables: {
      limit: DRIVER_LIMIT,
      offset: 0,
      query: externalSearchQuery === undefined ? (debouncedDriverSearchQuery || undefined) : undefined,
    },
    fetchPolicy: 'network-only',
    skip: providedDrivers !== undefined,
  });

  const drivers: Driver[] = useMemo(() => {
    if (providedDrivers !== undefined) {
      return providedDrivers;
    }
    if (!driversData?.fetchAllCouriers?.items) return [];
    return driversData.fetchAllCouriers.items.map((item: any) => {
      const firstName = (item.firstName || '').trim();
      const lastName = (item.lastName || '').trim();
      let name = '';
      if (firstName && lastName) {
        name = `${firstName} ${lastName}`;
      } else if (firstName) {
        name = firstName;
      } else if (lastName) {
        name = lastName;
      } else {
        // Use mobile number or ID instead of 'Unknown' so search can match
        name = item.mobileNumber || (item.id ? `Driver ${item.id}` : 'Unknown');
      }
      return {
        id: item.id,
        firstName: firstName,
        lastName: lastName,
        name: name,
        mobileNumber: item.mobileNumber || '',
        phone: item.mobileNumber || '',
        status: item.isOnline
          ? (item.isBusy ? 'Busy' : 'Online')
          : 'Idle' as Driver['status'],
        latitude: item.latitude,
        longitude: item.longitude,
        avatarUrl: item.avatarUrl,
        rating: item.rating,
        vehicleType: item.vehicleType,
        isBanned: item.isBanned,
        lastSeen: item.lastSeen,
      };
    });
  }, [providedDrivers, driversData]);

  const filteredDrivers = useMemo(() => {
    // Return all drivers without any filtering
    return drivers;
  }, [drivers]);

  const hasMoreDrivers = useMemo(() => {
    if (providedDrivers !== undefined) {
      return providedHasMoreDrivers;
    }
    const totalLoaded = driversData?.fetchAllCouriers?.items?.length || 0;
    const totalCount = driversData?.fetchAllCouriers?.totalCount || 0;
    return totalLoaded < totalCount;
  }, [providedDrivers, providedHasMoreDrivers, driversData]);

  const isLoading = providedDrivers !== undefined ? providedDriversLoading : driversLoading;

  useEffect(() => {
    const handleScroll = () => {
      if (!driverListRef.current || isLoading || isFetchingMoreRef.current || isLoadingMoreDrivers) return;
      const { scrollTop, scrollHeight, clientHeight } = driverListRef.current;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        if (providedDrivers !== undefined && onLoadMoreDrivers && hasMoreDrivers) {
          isFetchingMoreRef.current = true;
          onLoadMoreDrivers();
          setTimeout(() => {
            isFetchingMoreRef.current = false;
          }, 500);
          return;
        }

        if (providedDrivers === undefined) {
          const totalLoaded = driversData?.fetchAllCouriers?.items?.length || 0;
          const totalCount = driversData?.fetchAllCouriers?.totalCount || 0;
          if (totalLoaded < totalCount && fetchMoreDrivers) {
            isFetchingMoreRef.current = true;
            setIsLoadingMoreDrivers(true);
            fetchMoreDrivers({
              variables: {
                offset: totalLoaded,
                limit: DRIVER_LIMIT,
                query: externalSearchQuery === undefined ? (debouncedDriverSearchQuery || undefined) : undefined,
              },
              updateQuery: (prev: any, { fetchMoreResult }: any) => {
                if (!fetchMoreResult?.fetchAllCouriers) {
                  isFetchingMoreRef.current = false;
                  setIsLoadingMoreDrivers(false);
                  return prev;
                }

                const existingIds = new Set(prev.fetchAllCouriers.items.map((item: any) => item.id));
                const newItems = fetchMoreResult.fetchAllCouriers.items.filter(
                  (item: any) => !existingIds.has(item.id)
                );

                isFetchingMoreRef.current = false;
                setIsLoadingMoreDrivers(false);
                return {
                  fetchAllCouriers: {
                    ...fetchMoreResult.fetchAllCouriers,
                    items: [
                      ...prev.fetchAllCouriers.items,
                      ...newItems,
                    ],
                  },
                };
              },
            }).catch(() => {
              isFetchingMoreRef.current = false;
              setIsLoadingMoreDrivers(false);
            });
          }
        }
      }
    };

    const listElement = driverListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      return () => listElement.removeEventListener('scroll', handleScroll);
    }
  }, [isLoading, driversData, debouncedDriverSearchQuery, fetchMoreDrivers, isLoadingMoreDrivers, providedDrivers, onLoadMoreDrivers, hasMoreDrivers, externalSearchQuery]);

  const handleDriverToggle = (driverId: string, driverName?: string) => {
    const isCurrentlySelected = selectedDriverIds.includes(driverId);
    const name = driverName || drivers.find(d => d.id === driverId)?.name || 'this driver';

    if (isCurrentlySelected) {
      const confirmed = window.confirm(`Are you sure you want to unselect ${name}?`);
      if (!confirmed) return;

      const newIds = selectedDriverIds.filter(id => id !== driverId);
      onDriverIdsChange(newIds);
    } else {
      const newIds = [...selectedDriverIds, driverId];
      onDriverIdsChange(newIds);
    }
  };

  const handleSelectAll = () => {
    const allIds = filteredDrivers.map(d => d.id);
    onDriverIdsChange(allIds);
  };

  const handleUnselectAll = () => {
    if (selectedDriverIds.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to unselect all ${selectedDriverIds.length} selected driver${selectedDriverIds.length > 1 ? 's' : ''}?`
    );

    if (confirmed) {
      onDriverIdsChange([]);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-orange-50 flex items-center justify-center flex-shrink-0">
            <PeopleIcon sx={{ fontSize: '16px', color: '#F97316' }} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 leading-tight">Drivers</h2>
            <p className="text-xs text-gray-500 leading-tight">Select drivers to auto-assign</p>
          </div>
        </div>
        {selectedDriverIds.length > 0 && (
          <div className="px-2 py-0.5 bg-primaryColor/10 text-primaryColor rounded-md text-[10px] font-semibold">
            {selectedDriverIds.length}
          </div>
        )}
      </div>

      {/* Compact Selected Drivers Display */}
      {selectedDriverIds.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 rounded-md border border-gray-200 max-h-24 overflow-y-auto">
            {selectedDriverIds.map((driverId) => {
              const driver = drivers.find(d => d.id === driverId);
              if (!driver) return null;
              return (
                <div
                  key={driverId}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-gray-300 rounded-full text-[10px] hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-700 truncate max-w-[80px]">{driver.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const driver = drivers.find(d => d.id === driverId);
                      const driverName = driver?.name || 'this driver';
                      const confirmed = window.confirm(`Are you sure you want to unselect ${driverName}?`);
                      if (confirmed) {
                        handleDriverToggle(driverId);
                      }
                    }}
                    className="ml-0.5 hover:bg-gray-200 rounded-full p-0.5 transition-colors flex-shrink-0"
                    type="button"
                  >
                    <CloseIcon sx={{ fontSize: '12px', color: '#6B7280' }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search and Filter Controls with Better Spacing */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 mb-3">
        <div className="flex items-center flex-wrap gap-2.5">
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon sx={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#9CA3AF' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, or ID..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-primaryColor focus:border-primaryColor outline-none bg-white transition-all"
            />
          </div>
          <Autocomplete
            options={[
              { value: 'Online', label: 'Online' },
              { value: 'Busy', label: 'Busy' },
              { value: 'Idle', label: 'Idle' },
            ]}
            getOptionLabel={(option) => option.label || ''}
            value={statusFilter ? { value: statusFilter, label: statusFilter } : null}
            onChange={(event, newValue) => {
              setStatusFilter(newValue ? newValue.value : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All Status"
                size="small"
                sx={{
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    padding: '0.5rem',
                    height: '32px',
                    borderRadius: '0.5rem',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#D1D5DB',
                      borderRadius: '0.5rem',
                    },
                    '&:hover fieldset': {
                      borderColor: '#9CA3AF',
                      borderRadius: '0.5rem',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3B82F6',
                      borderWidth: '2px',
                      borderRadius: '0.5rem',
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                <span className="text-xs">{option.label}</span>
              </li>
            )}
            noOptionsText="No status found"
          />
          {filteredDrivers.length > 0 && (
            <Tooltip title="Select all drivers" arrow>
              <button
                onClick={handleSelectAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all whitespace-nowrap shadow-sm"
              >
                Select All
              </button>
            </Tooltip>
          )}
          {selectedDriverIds.length > 0 && (
            <Tooltip title="Unselect all selected drivers" arrow>
              <button
                onClick={handleUnselectAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all whitespace-nowrap shadow-sm"
              >
                Clear All
              </button>
            </Tooltip>
          )}
          {(searchQuery || statusFilter) && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-300 rounded-lg transition-all shadow-sm"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Compact Driver List */}
      {isLoading && filteredDrivers.length === 0 ? (
        <div
          ref={driverListRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[450px] overflow-y-auto pr-1"
        >
          {[...Array(8)].map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="group relative flex flex-col p-3 border-2 border-gray-200 rounded-lg animate-pulse"
            >
              <div className="absolute top-2 right-2">
                <div className="w-4 h-4 rounded border border-gray-200 bg-gray-100"></div>
              </div>
              <div className="flex flex-col">
                <div className="w-full mb-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-2.5 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-16 mb-2"></div>
                  <div className="h-2 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredDrivers.length === 0 ? (
        <div className="text-center py-8 bg-white/60 rounded-md border border-gray-200/60">
          <SearchIcon sx={{ fontSize: '32px', color: '#9CA3AF', marginBottom: '4px' }} />
          <p className="text-xs font-medium text-gray-600">No drivers found</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div
          ref={driverListRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-[450px] overflow-y-auto pr-1"
        >
          {filteredDrivers.map((driver) => {
            const isSelected = selectedDriverIds.includes(driver.id);
            const isAlreadyAssigned = assignedDriverIds.includes(driver.id);
            const assignmentStatus = assignedDriverStatuses[driver.id] || '';
            const isCancelled = assignmentStatus === 'cancelled';
            // Cancelled assignments should be selectable again, so exclude them from disabled state
            const isDisabled = remainingSlots !== undefined && remainingSlots <= 0 && !isSelected || (isAlreadyAssigned && !isCancelled);
            
            const cardContent = (
              <label
                className={`group relative flex flex-col p-3 border-2 rounded-lg transition-all ${isSelected
                  ? 'border-primaryColor bg-primaryColor/10 shadow-md cursor-pointer'
                  : isDisabled
                    ? 'border-gray-400 bg-gray-100 shadow-md cursor-not-allowed'
                    : isCancelled
                      ? 'border-red-400 bg-red-50 hover:border-red-500 hover:bg-red-100 shadow-md cursor-pointer'
                      : 'border-gray-200 hover:border-primaryColor/50 hover:bg-white hover:shadow-md cursor-pointer'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {
                    if (!isDisabled) {
                      handleDriverToggle(driver.id, driver.name);
                    }
                  }}
                  disabled={isDisabled}
                  className="sr-only"
                />

                <div className="absolute top-2 right-2 z-10">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isSelected
                    ? 'border-primaryColor bg-primaryColor'
                    : isDisabled
                      ? 'border-gray-500 bg-gray-400'
                      : isCancelled
                        ? 'border-red-500 bg-red-400 group-hover:border-red-600 group-hover:bg-red-500'
                        : 'border-gray-300 bg-white group-hover:border-primaryColor/50'
                    }`}>
                    {isSelected ? (
                      <CheckBoxIcon sx={{ fontSize: '14px', color: 'white' }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon sx={{ fontSize: '14px', color: isCancelled ? '#FFFFFF' : (isAlreadyAssigned ? '#FFFFFF' : (isDisabled ? '#9CA3AF' : '#9CA3AF')) }} />
                    )}
                  </div>
                </div>

                {isAlreadyAssigned && (
                  <div className={`absolute bottom-2 right-2 text-white text-[9px] font-bold px-2 py-1 rounded-md z-10 shadow-sm border ${isCancelled
                    ? 'bg-red-600 border-red-700'
                    : 'bg-gray-600 border-gray-700'
                    }`}>
                    {isCancelled ? 'CANCELLED' : 'ASSIGNED'}
                  </div>
                )}

                <div className={`flex flex-col ${isAlreadyAssigned ? 'opacity-90' : ''}`}>
                  {/* ID, Name, and Phone */}
                  <div className="mb-2">
                    {driver.id && (
                      <div className="mb-1.5">
                        <span className={`text-xs font-mono font-medium ${isCancelled
                          ? 'text-red-600'
                          : isAlreadyAssigned
                            ? 'text-gray-500'
                            : 'text-gray-600'
                          }`}>
                          ID {driver.id}
                        </span>
                      </div>
                    )}

                    <div className="mb-1.5">
                      <p className={`font-semibold text-sm truncate ${isSelected
                        ? 'text-primaryColor'
                        : isCancelled
                          ? 'text-red-700'
                          : isAlreadyAssigned
                            ? 'text-gray-700'
                            : 'text-gray-900'
                        }`}>
                        {driver.name}
                      </p>
                    </div>

                    {driver.phone && (
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon sx={{ fontSize: '12px', color: isCancelled ? '#DC2626' : (isAlreadyAssigned ? '#6B7280' : '#6B7280') }} />
                        <p className={`text-xs truncate ${isCancelled
                          ? 'text-red-600'
                          : isAlreadyAssigned
                            ? 'text-gray-600'
                            : 'text-gray-600'
                          }`}>{driver.phone}</p>
                      </div>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div className="mb-2">
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-semibold border ${isCancelled
                        ? 'bg-red-100 text-red-700 border-red-300'
                        : isAlreadyAssigned
                          ? 'bg-gray-200 text-gray-700 border-gray-300'
                          : getStatusColor(driver.status)
                        }`}
                    >
                      {driver.status}
                    </span>
                  </div>

                  {/* Rating, Vehicle Type, and Last Seen */}
                  <div className="space-y-1.5">
                    {(driver.rating !== undefined && driver.rating !== null) || (driver.vehicleType !== undefined && driver.vehicleType !== null) ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        {driver.rating !== undefined && driver.rating !== null && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs">⭐</span>
                            <span className={`text-xs font-medium ${isCancelled
                              ? 'text-red-600'
                              : isAlreadyAssigned
                                ? 'text-gray-600'
                                : 'text-gray-700'
                              }`}>{driver.rating.toFixed(1)}</span>
                          </div>
                        )}
                        {driver.vehicleType !== undefined && driver.vehicleType !== null && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs">🚗</span>
                            <span className={`text-xs font-medium ${isCancelled
                              ? 'text-red-600'
                              : isAlreadyAssigned
                                ? 'text-gray-600'
                                : 'text-gray-700'
                              }`}>{driver.vehicleType}</span>
                          </div>
                        )}
                      </div>
                    ) : null}

                    {driver.lastSeen && (
                      <div>
                        <p className={`text-[10px] ${isCancelled
                          ? 'text-red-500'
                          : isAlreadyAssigned
                            ? 'text-gray-500'
                            : 'text-gray-500'
                          }`}>
                          Last seen: {formatDate(driver.lastSeen)}
                        </p>
                      </div>
                    )}

                    {driver.isBanned && (
                      <div className="flex justify-start">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium bg-red-100 text-red-700 border border-red-200">
                          Banned
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </label>
            );

            // Wrap cancelled cards with tooltip
            if (isCancelled) {
              return (
                <Tooltip
                  key={driver.id}
                  title="This assignment was cancelled. You can select this driver again to reassign them."
                  arrow
                  placement="top"
                >
                  {cardContent}
                </Tooltip>
              );
            }

            return cardContent;
          })}
          {(isLoading || isLoadingMoreDrivers) && filteredDrivers.length > 0 && (
            <>
              {[...Array(4)].map((_, index) => (
                <div
                  key={`skeleton-more-${index}`}
                  className="group relative flex flex-col p-3 border-2 border-gray-200 rounded-lg animate-pulse"
                >
                  <div className="absolute top-2 right-2">
                    <div className="w-4 h-4 rounded border border-gray-200 bg-gray-100"></div>
                  </div>
                  <div className="flex flex-col">
                    <div className="w-full mb-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-2.5 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-6 bg-gray-200 rounded w-16 mb-2"></div>
                      <div className="h-2 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-span-full flex items-center justify-center py-2">
                <CircularProgress size={16} sx={{ color: '#6366F1', mr: 1 }} />
                <span className="text-[10px] text-gray-500 font-medium">Loading...</span>
              </div>
            </>
          )}
          {!hasMoreDrivers && filteredDrivers.length > 0 && !isLoading && !isLoadingMoreDrivers && (
            <div className="col-span-full text-center py-2 text-[10px] text-gray-400">
              All drivers loaded
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreAssignedDriversSelector;

